import { openVipPlayTimes, openFileProcessor } from '@remote/utils/dialog'
// store.ts
import { InjectionKey } from 'vue'
import { createStore, Store, useStore as baseUseStore } from 'vuex'
import type { IState, ITaskRes, ITask, IRunner, IRunnerParamsDownload, IErrorRes, ITaskFile } from '@remote/type'
// import * as api from "@/api";
// import { request } from '@remote/api/request'
import { getInnerApiProxy } from '@remote/api/proxy'
import { uniqueObject, str2date, isWeixin, judgeClient, isTruePlayFormat, isNativeApp, nativeCall, isIframe, isMiniProgramPromise, setLocalValue, getLocalValue, removeLocalValue, toast } from '@remote/utils'
import { getReferVisit, stat } from '@remote/utils/stat'
import {
  USER_DOWNLOAD,
  USER_DOWNLOAD_URL,
  USER_RUNNER,
  TASK_TYPE,
  MAX_DEVICE_ONLINE_NUM,
  THE_VERSION_CODE,
  DOWNLOAD_URL,
  MIN_IOS_VERSION_CODE,
  TASK_COMPLETE,
  TASK_RUNNING,
  isIOS as isIOSUA,
  ExcludePlatforms,
  MapMiniProgramHome,
  ERROR_DESC_MAP,
  VIP_USER_PLAY_FILE_SIZE,
  USER_PLAY_FILE_SIZE,
  EXPIRE_DATE_LIST,
  OldJumpPlayVideoPlatforms,
  FactoryPathMap,
  SUPPORT_VIDEO_PLAY_CODE,
  XL_BOX_PLATFORM,
} from '@remote/constant'
import Icons from '@remote/utils/static-icons'
import router from '../router'
import global from './constantStore'
import { isAndroidHezi, isIOSHezi } from '@remote/utils/platform'
import { isAndroidNative as isAndroid, isIOSNative as isIOS, sendMessage, callIOS, checkMessage, callAndroid } from '@xunlei/universal-native-api'
import sso, { getSSOUser, auth } from '@remote/utils/sso'
import '@remote/utils/genDeviceUtil'
import { getConfig, getIOSUserInfo } from '@remote/api/index'
import pkg from '../../package.json'
import { Devices, Users, PersistPlugin } from './persist'
import { apiOrigin, TeamSpeedApi, setApiEnv, APP_NAME, isSeparateAccount } from '@remote/api/config'
import dayjs from 'dayjs'
import { DAY } from '@shared/constants/time'
import { toISOStringWithTimezone } from '@shared/utils/time'
import { useEnvStore } from '@remote/store/env'
import packageJSON from '@remote/../package.json'
import { parseVersionToNumber, safeJSONParse } from '@shared/utils'
import request from '@remote/api/adapter'
import { playInThunder } from '@remote/utils/cinema'
import { requestAnimationFrame, clearTimeout } from '@remote/utils/base'
import { isImageFile } from '@remote/utils/file-helper'
import { reportPlayVideo, logInfo } from '@remote/utils/logger'
import bindphone from '@remote/views/settings/account/bindphone'
import hzAndroidBridge from '@remote/utils/jsbridge/hezi/android'
import type { IHeZiUserInfo } from '@remote/utils/jsbridge/hezi/types/android'
import { isHeziPCClient, playVideo, isNearBy } from '@remote/utils/hezi-pc-client'
// define injection key
export const key: InjectionKey<Store<IState>> = Symbol()

let runnerTimer: NodeJS.Timeout | null = null
let taskTimmer: NodeJS.Timeout | null = null
let isFirst = true

export const store = createStore<IState>({
  modules: {
    Devices,
    Users,
  },
  plugins: [PersistPlugin],
  state: {
    all: {},
    url2IdMap: {},
    user: null,
    runners: [],
    /**
     * curDeviceRunner 有两个数据来源，一个是getRunner，一个是watch接口。
     * 登录绑定后，会同时轮询getRunner接口和watch接口。
     * 当watch接口能正常返回时，getRunner不再设置state中的curDeviceRunner。
     * 此时getRunner只做获取设备列表之用，展示在「我的」-「设备列表」中
     */
    curDeviceRunner: { status: 2, name: '迅雷盒子' } as Partial<IRunner>, // 当前在线设备
    space: '', // 当前设备id
    tasks: [], // 进行中的任务
    tasksDone: [], // 已完成的任务
    taskType: 'ing',
    firstLoadTasks: {
      'ing': true,
      'done': true,
      'file': true,
    },
    files: [],
    parentIds: [],
    dialog: {
      show: false,
      title: '',
      tip: '',
      cancelTxt: '',
      okTxt: '',
      action: '',
      cancel: '',
      hasCheck: false,
      more: '',
      showLoader: false,
      verticalFooter: false,
    },
    deleteCompletely: false,
    isSelect: false,
    selects: [], // 选中的下载任务
    deleteTaskList: [], // 删除的下载任务
    status: 0, // task.phase === 'PHASE_TYPE_RUNNING' ? 1 : 0
    isAllSelect: false,
    loading: 1,
    checking: false,
    nextPageToken: '',
    action: '',
    inListFiles: [],
    connecting: false,
    fromSpace: '',// 绑定流程中待绑定的设备
    guid: '',
    createFrom: '',
    visibleHuddle: true,
    isShowVipPop: false, //抱团次数结束弹跳VIP窗
    teamNumber: 0, // 抱团人数
    teamTaskIds: [], // 抱团任务ID
    teamTaskId: '', // 当前要显示的抱团任务ID
    teamLeftTimes: 0, // 抱团剩余次数
    huddleNum: 3, // 挂条展示三次
    today: new Date().toLocaleDateString(),
    showLastCount: false,
    panCliFull: false, // NAS体验功能开关
    privilegeAuth: false,
    visiblePathChoose: false, // 目录选择框显示
    paths: [], // 路径id列表
    pathIndex: 0, // 路径id的序号
    pathFolderId: '', // 选择下载的路径id
    pathFolderName: '', // 选择下载路径的名称
    rootSplitPath: '', //选择的磁盘目录
    toast: { // 全局 toast，在手机浏览器内使用
      message: '',
      duration: 3000,
    },
    currentSpped: 0,
    showDevicesControl: true,
    showSpeedControl: true,
    showPathControl: false,
    showQuitControl: true,
    queryAuth: {
      user_code: '',
      scope: 'pan user profile sso offline pan/xunlei/share/create',
      client_id: 'X9ibISwpIp8jQ4Ya',
    },
    platform: '',
    runnerSpace: '', // NAS厂商过滤条件
    // 这里的isNative指的是迅雷账号体系下的Native，即手机迅雷
    isNative: isAndroid || isIOS,
    justOne: 0, // 是否只显示当前一台设备
    isApp: isNativeApp(),
    showLoader: false,
    isMiniProgram: false,
    pop: {
      show: false,
      title: '',
      tip: '',
      okTxt: '',
      type: '',
    },
    showFilePop: false,
    pluginData: [],
    currentIframeUrl: '',
    currentIframeTitle: '',
    currentIframeShowClose: false,
    iosAppVersion: 0,
    expiredDay: '',
    cooperationScene: '',
    cooperationExpiresDialogVisiable: false,
    playPrivilege: {
      show: false,
      type: 0,
    },
    deviceInfoWatchData: {},
    cooperationShow: false,
    isSupportProxy: false,
    historyDownloadPath: [],
    trySpeedStatus: 'HIDDEN', //试用速度状态
  },
  getters: {
    isSuperVip (state): boolean {
      // 判断是否超级会员
      return !!(state.user && state.user.isVip && state.user.vipType === 5)
    },
    isLogin (state): boolean {
      return !!state.user
    },
    isVip (state): boolean {
      return !!(state.user && state.user.isVip)
    },
    isYear (state): boolean {
      return !!(state.user && state.user.isYear)
    },
    isYSvip (state): boolean {
      return !!(state.user && state.user.isVip && state.user.vipType === 5 && state.user.isYear)
    },
    userId (state): string {
      return state.user ? state.user.userID : '0'
    },
    curList (state): string[] {
      const fileMaps = {
        ing: 'tasks',
        done: 'tasksDone',
        file: 'files',
        bt: 'files',
      }
      const type = fileMaps[state.taskType]
      return state[type]
    },
    curAllSelect (state, getters): boolean {
      return state.selects.length === getters.curList.length
    },
    vipType (state): number {
      return state.user ? state.user.vipType : 0
    },
    runningList (state): string[] {
      //判断受否处于下载中
      return state.tasks.filter(id => {
        const task = state.all[id] as ITask
        return task.phase === 'PHASE_TYPE_RUNNING'
        //  && (task?.params?.status?.includes('running'))
      })
    },
    runnerPath (): IRunnerParamsDownload {
      return { path: '内置磁盘/我的下载', usage: 0, limit: 1000 }
    },
    runnerConfig (state, getters): any {
      const def = { speed_limit: -1, download_path: '/Download/' }
      let config: any = (state.curDeviceRunner && state.curDeviceRunner?.params?.device_config) || JSON.stringify(def)
      try {
        config = JSON.parse(config)
      } catch (err) {
        config = def
      }
      return config
    },
    lastSize (state, getters): number {
      return getters.runnerPath.limit - getters.runnerPath.usage
    },
    showPath (state, getters) {
      return getters.runnerPath.path + state.pathFolderName
    },
    showHeader (state) {
      // 只有微信浏览器和ios手雷不显示
      return state.isMiniProgram || !isWeixin()
    },
    showVip (state) {
      return state.platform !== 'hiksemi'
    },
    deviceTime (state) {
      return state.curDeviceRunner?.updated_time
    },
    isFullScreen (state) {
      // ios从147版本会支持从链接参数 noStatusBar 来支持全屏
      // window.webkit.messageHandlers.hdc 华为iOS
      return (isAndroid && location.search.includes('noStatusBar') || (isIOS && state.iosAppVersion >= 144) || (window?.webkit?.messageHandlers?.hdc))
    },
    appCanPlay (state, getters) {
      return (state.isApp || isAndroid || isIOS) && state.panCliFull
    },
    isXlBox (state) {
      return state.curDeviceRunner.params?.platform === XL_BOX_PLATFORM
    },
    isDownloadAvailable (state) {
      const access = state.curDeviceRunner?.params?.access
      // 如果有access，说明已经找到设备
      if (access) {
        return access.includes('user#download-url')
      }
      // 没有access的情况，可能没有找到设备，要展示为空的情况。
      return true
    },
  },
  mutations: {
    set (state, payload) {
      state.all[payload.id] = payload
    },
    update (state, payload) {
      for (const key in payload) {
        state[key] = payload[key]
      }
    },
    dialog (state, payload) {
      state.dialog = {
        showLoader: false,
        ...state.dialog,
        hasCheck: false,
        more: '',
        verticalFooter: false,
        ...payload,
      }
    },
    selTask (state, id) {
      let selects = state.selects
      if (selects.includes(id)) {
        selects = selects.filter(v => v !== id)
      } else {
        selects.push(id)
      }
      state.selects = selects
    },
    delete (state, ids) {
      if (state.taskType === 'ing') {
        const list = Array.isArray(ids)
          ? state.tasks.filter(v => !ids.includes(v))
          : state.tasks.filter(v => v !== ids)
        state.tasks = list
      } else {
        const list = Array.isArray(ids)
          ? state.tasksDone.filter(v => !ids.includes(v))
          : state.tasksDone.filter(v => v !== ids)
        state.tasksDone = list
      }
    },
    updateFile (state, datas) {
      const { id, data } = datas
      state.all[id] = {
        ...state.all[id],
        ...data,
      }
    },
    updatePaths (state, data) {
      const { key, paths } = data
      state.paths[key] = paths
    },
    toast (state, message) {
      if (state.dialog && state.dialog.show) return
      // if (state.isNative) return toast(message)
      state.toast.message = message
      setTimeout(() => {
        state.toast.message = ''
      }, state.toast.duration)
    },
    pop (state, payload) {
      state.pop = {
        ...state.pop,
        ...payload,
      }
    },
    clearTaskTimer () {
      if (taskTimmer) clearTimeout(taskTimmer)
    },
  },
  actions: {
    async checkVersion ({ commit, getters, dispatch }) {
      if (isAndroid) {
        const info: any = await sendMessage({ platform: 'android', name: 'xlGetAppMetaData' }, { callbackTimeout: 5000 })
        console.log('deviceInfo', info)
        if (info) {
          commit('update', {
            guid: info.deviceGuid,
          })
          global.device = info
          global.guid = info.deviceGuid
          global.peerid = info.peerID
          global.versionCode = info.versionCode
          if (info.appVersion && info.appVersion <= THE_VERSION_CODE) {
            stat({ action: 'download_shoulei_page_show' })
            setTimeout(() => (location.href = DOWNLOAD_URL), 500)
          }
        }
      }
      if (isIOS) {
        const res = await checkMessage({ platform: 'ios', name: 'xlAjax' })
        console.log('checkMessage xlAjax', res)
        if (res && res.exist) {
          global.iosRequest = true
        }
      }
    },
    async login ({ commit, dispatch, state, getters }, params = {}) {
      try {
        if (!navigator.onLine) return false
        const { forceLogin = 0, checkRunner = true } = params
        isFirst && stat({ action: 'h5_loading_page_show' })
        commit('update', { connecting: true })
        if (isIOS) {
          try {
            const [err, res] = await sendMessage({
              platform: 'ios',
              name: 'userInfo',
            }, { callbackTimeout: 1000 * 30 })
            console.log('ios-userInfo: ', res)
            commit('update', { connecting: false })
            if (res && (res.authorize || res.sessionId)) {
              // 已登录
              const Authorize = 'Bearer ' + res.authorize
              global.Authorize = Authorize
              const user = res
              if (!isIOSHezi) {
                try {
                  console.log('非盒子客户端请求nas用户信息:', res.userId)
                  // 获取nas绑定账号信息
                  const IOSUserInfo = await getIOSUserInfo(res.userId)
                  const avatar = IOSUserInfo?.avatar
                  const expire = IOSUserInfo?.vip?.expire
                  if (expire) {
                    // 没有绑定过nas的帐号没有头像地址
                    user.expiresAt = str2date(expire)
                  }
                  if (avatar) {
                    user.avatarURL = avatar
                  }
                } catch (err: any) {
                  console.log('ios 请求失败', err.message)
                }
              }
              const appVersion = Number(res.appVersion && res.appVersion.replace(/\./g, '') || 0)
              commit('update', { user, guid: res.deviceId, iosAppVersion: appVersion })
              const userId = String(res.userId)
              global.vipType = res.vipType
              global.isVip = res.isVip
              global.userId = userId
              global.guid = res.deviceId
              global.peerid = res.peerId
              global.device = {
                deviceId: res.deviceId,
                userId,
                userName: res.nickName,
                versionCode: res.buildVersion,
                appVersion: res.appVersion,
              }
              commit('Users/set', user)
              // if (appVersion < MIN_IOS_VERSION_CODE) {
              //   console.log('需要安装新版本 :>> ', res.appVersion)
              //   stat({ action: 'download_shoulei_page_show' })
              //   router.push({
              //     path: '/upgrade',
              //   })
              //   return
              // }
              checkRunner && setTimeout(() => dispatch('getRunner'), 5000) && dispatch('getRunner')
              setTimeout(() => dispatch('getConfig'), 1000)
              return true
            } else {
              // 未登录
              const [err, res] = await sendMessage(
                {
                  platform: 'ios',
                  name: 'openLoginPage',
                },
                { callbackTimeout: 60000 * 30 },
              )
              if (res && res.result === 'success') {
                dispatch('login')
                return false
              }
            }
            return
          } catch (error) {
            console.warn('ios-userInfo: ', error)
            commit('update', { connecting: false })
          }
        }
        if (isAndroid) {
          const res: any = await sendMessage(
            {
              platform: 'android',
              name: 'xlGetUserInfo',
              params: {
                forceLogin,
              },
            },
            { callbackTimeout: 1000 * 30 },
          )
          console.log('login', res)
          commit('update', { connecting: false })
          if (res && res.isLogin) {
            const user = {
              ...res.userInfo,
              expiresAt: str2date(res.userInfo.vipExpirationDate),
            }
            commit('update', { user })
            checkRunner && setTimeout(() => dispatch('getRunner'), 5000) && dispatch('getRunner')
            global.vipType = res.vipType
            global.isVip = res.isVip
            global.userId = res.userInfo.userID
            commit('Users/set', user)
            setTimeout(() => dispatch('getConfig'), 1000)
            return true
          } else {
            return false
          }
        }
        if (isAndroidHezi) {
          const logs = (..._args) => {
            console.log('[Login]', ..._args)
          }
          const userInfo: IHeZiUserInfo = await new Promise((resolve, reject) => {
            hzAndroidBridge.callHandler('getUserInfo', (err, result) => {
              logs('[getUserInfo]:', err, result)
              if (err) {
                reject(err)
              } else {
                resolve(result)
              }
            })
          })
          logs('获取用户信息:', userInfo)
          if (userInfo && userInfo.userId) {
            const user: IState['user'] = {
              userID: userInfo.userId,
              avatarURL: userInfo.avatar,
              isVip: !!userInfo.hzInfo?.vip.isvip,
              isYear: !!userInfo.hzInfo?.vip.isyear,
              nickName: userInfo.nickName,
              phoneNumber: userInfo.phoneNumber,
              sessionID: '',
              userNo: '',
              verify: '',
              vipExpirationDate: userInfo.hzInfo.vip.expire_time,
              vipLevel: userInfo.vipLevel,
              vipType: 0,
              visitorID: 0,
              jumpKey: '',
              // expiresAt: str2date(userInfo.hzInfo.vip.expire_time),
            }
            commit('update', { user })
            checkRunner && setTimeout(() => dispatch('getRunner'), 5000) && dispatch('getRunner')
            global.vipType = user.vipType
            global.isVip = user.isVip
            global.userId = user.userID
            commit('Users/set', user)
            setTimeout(() => dispatch('getConfig'), 1000)
            return true
          } else {
            return false
          }
        }
        if (!state.isNative) { // TODO: 跟志超确认这里的必要性
          const isLogined = await sso.init()
          console.log('isLogined', isLogined, 'forceLogin', forceLogin)
          if (!isLogined) {
            if (forceLogin) {
              if (window.native) {
                window.native.CallNativeFunction('GetUserID', async (status: number, userID: string) => {
                  console.log('客户端登录态: userid:', userID)
                  if (userID !== '0') {
                    await sso.signIn()
                  } else {
                    window.native.CallNativeFunction('ShowLoginDlg')
                    const id = window.native.AttachNativeEvent('OnLoginSuc', async () => {
                      console.log('收到客户端登录事件')
                      window.native.DetachNativeEvent('OnLoginSuc', id)
                      await sso.signIn()
                    })
                  }
                })
              } else {
                await sso.signIn()
              }
            } else {
              commit('update', { connecting: false })
              return false
            }
          } else {
            try {
              const user = await getSSOUser()
              console.log('user', user.userID, user)
              if (window.native) {
                window.native.CallNativeFunction('GetUserID', async (status: number, userID: string) => {
                  // 客户端登录态和页面登录态不相同时，signout
                  console.log('判断双端登录态是否相同：客户端：', userID, '页面：', user.userID)
                  if (user.userID !== userID) {
                    await sso.oAuth2Client.setCredentials()
                    await sso.signIn()
                  } else {
                    // 监听登出事件
                    console.log('监听登出事件')
                    window.native.AttachNativeEvent('OnLogout', async () => {
                      console.log('收到客户端登出事件')
                      const redirectUrl = 'https://hezi.xunlei.com/yc/login'
                      await sso.signOut(redirectUrl)
                    })
                    console.log('监听用户信息变化事件')
                    window.native.AttachNativeEvent('OnUserInfoChange', async () => {
                      console.log('收到用户信息变化事件')
                      const user = await getSSOUser()
                      commit('update', { user })
                    })
                  }
                })
              }
              // commit('update', { connecting: false })
              global.peerid = global.deviceSign
              global.device = {
                deviceId: global.deviceID,
                userId: user.userID,
                userName: user.nickName,
                versionCode: THE_VERSION_CODE,
                appVersion: pkg.version,
              }
              commit('update', { user })
              console.log('checkRunner', checkRunner)
              checkRunner && setTimeout(() => dispatch('getRunner'), 5000) && dispatch('getRunner')
              global.vipType = user.vipType
              global.isVip = user.isVip
              global.userId = user.userID
              commit('Users/set', user)
              setTimeout(() => dispatch('getConfig'), 1000)
              if (!user.phoneNumber && isSeparateAccount) bindphone()
              return true
            } catch (error) {
              console.log('error', error)
            }
          }
        }
        commit('update', { connecting: false })
        return false
      } catch (error) {
        console.log('store error', error)
        return false
      }
    },
    async getRunner ({ commit, dispatch, state }, params = {}) {
      const { repeatCount = 20, afterGetRunnerSucceessHideDialog = false } = params
      const envStore = useEnvStore()

      commit('update', { checking: true })
      if (runnerTimer) clearTimeout(runnerTimer)

      let curDeviceRunner: Partial<ITask> = getLocalValue('curDeviceRunner') || {}

      const yesterdayTimestamp = toISOStringWithTimezone(new Date(Date.now() - 1 * DAY))

      const [err, res]: [IErrorRes | null, ITaskRes] = await request('get/device/list', {
        space: state.runnerSpace,
        userid: Number(state?.user?.userID),
        filters: repeatCount > 0 ? `{"updated_time":{"gt":"${yesterdayTimestamp}"}}` : '',
      })

      // console.log('getRunner 1', err, res)
      if (err) {
        if (err.error === 'unauthenticated') {
          if (isSeparateAccount) {
            // 独立账号未登录直接跳转登录页面，不再进行授权登录
            router.replace('/login')
          } else {
            dispatch('login', { forceLogin: true })
          }
          return commit('update', { status: 0, loading: 0, checking: false, connecting: false })
        }
        if (!state.runners.length) commit('update', { checking: false, connecting: false, status: 0, loading: 0 })

        // commit('toast', err.error_description)
        isFirst = false
        runnerTimer = setTimeout(
          () => {
            const requestIdleCallback = window.requestIdleCallback || requestAnimationFrame
            requestIdleCallback(() => dispatch('getRunner', { autoTask: true, repeatCount: 0 }))
          },
          Math.min(res?.expires_in, 5) * 1000,
        )
        return false
      }

      if (res.error === 'unauthenticated') {
        if (isSeparateAccount) {
          // 独立账号未登录直接跳转登录页面，不再进行授权登录
          router.replace('/login')
        } else {
          dispatch('login', { forceLogin: true })
        }
        return commit('update', { status: 0, loading: 0, checking: false, connecting: false })
      }

      let tasks = res.tasks
      // if (!state.isApp) {
      //   // 在手机浏览器和手雷内不能显示合作方设备
      //   tasks = tasks.filter(v => ExcludePlatforms.indexOf(v.params.platform || 'hiksemi') < 0)
      // }

      if (state.justOne && state.fromSpace) {
        tasks = tasks.filter(v => v.params.target === state.fromSpace)
      }
      // 如果在PC里打开，需要把本身的PC设备隐藏掉。这里因为一个账号同时只能登录一个PC客户端，所以直接判断平台过滤即可。
      if (envStore.isInPC) {
        tasks = tasks.filter(v => v.space !== 'platform#pcxllite')
      }
      // console.log('getRunner 2', err, res)
      if (tasks && tasks.length) {
        let list: ITask[] = []
        for (const i in tasks) {
          const task = tasks[i]
          const d = new Date(task.updated_time).getTime()
          const since = new Date().getTime() - d
          list.push({
            ...task,
            target: task.params.target,
            status: task.status || (task.phase === 'PHASE_TYPE_RUNNING' ? 1 : 0),
            since,
          })
        }
        if (list.length === 0) return
        list = list
          // .sort((a, b) => new Date(b.updated_time).getTime() - new Date(a.updated_time).getTime())
          .sort((a, b) => b.status - a.status)
        list = uniqueObject(list, 'target')

        //第一个在线设备的下标
        let firstOnlineDeviceIndex = 0
        if (isFirst) {
          firstOnlineDeviceIndex = list.findIndex(v => v.status === 1)
          firstOnlineDeviceIndex = firstOnlineDeviceIndex > -1 ? firstOnlineDeviceIndex : 0 // 没有在线设备的话就取第一个设备（最近在线)
        }

        if (state.fromSpace) {
          const runnerWithSameSpace = list.find(v => v.params.target === state.fromSpace)
          console.log('runnerWithSameSpace', runnerWithSameSpace, list)
          curDeviceRunner = runnerWithSameSpace?.phase === 'PHASE_TYPE_RUNNING' ? runnerWithSameSpace : list[0] || {}
        } else {
          if (curDeviceRunner?.id) {
            const oIndex = list.findIndex(v => v.id === curDeviceRunner.id)
            curDeviceRunner = (oIndex > -1 && list[oIndex].status === 1) ? list[oIndex] : list[firstOnlineDeviceIndex]
          } else {
            curDeviceRunner = list[firstOnlineDeviceIndex]
          }
        }

        setLocalValue('curDeviceRunner', curDeviceRunner)
        if (state.fromSpace && curDeviceRunner?.status !== 1 && repeatCount > 0) {
          // 从扫码过来，可能设备还未启动，则需要重试
          commit('update', { connecting: true })
          console.warn('getRunner - 3s后重试(runner不为running)，剩余次数', repeatCount)
          return setTimeout(() => dispatch('getRunner', { afterGetRunnerSucceessHideDialog, repeatCount: repeatCount - 1 }), 3000)
        }
        const runners = list.map(v => {
          commit('set', v)
          return v.id
        })
        isFirst &&
          stat({
            action: 'bind_device_num',
            data: { num: runners.length },
          })
        commit('update', {
          checking: false,
          connecting: false,
          status: curDeviceRunner?.status,
          space: curDeviceRunner?.params?.target,
          curDeviceRunner,
          loading: 0,
        })

        envStore.updateEnv({
          platform: curDeviceRunner?.params?.platform as Platform,
          plm: curDeviceRunner?.params?.plm as PlatformShort,
          space: curDeviceRunner?.params?.target,
        })

        global.isXlBox = curDeviceRunner?.params?.platform === XL_BOX_PLATFORM
        global.fromSpace = curDeviceRunner?.params?.target || ''
        global.curDeviceConfig = curDeviceRunner?.params?.device_config
        if (curDeviceRunner?.params?.platform === XL_BOX_PLATFORM) setApiEnv('heziDevelopment')

        if (afterGetRunnerSucceessHideDialog) {
          commit('dialog', { show: true, title: '温馨提示', tip: '', cancelTxt: '', okTxt: '我知道了', action: 'tip-guide' })
        }

        if (state.runners.length !== runners.length) {
          if (isFirst && curDeviceRunner?.status && !location.href.includes('user_code')) {
            isFirst = false
          } else if (curDeviceRunner?.status && [-1, 0].includes(curDeviceRunner?.status)) {
            commit('toast', '当前设备已离线')
            stat({
              action: 'reconnect_device_page_show',
              data: { errorcode: 'no_connect_long_time' },
            })
          }
        }

        commit('update', { runners, connecting: false })

        runnerTimer = setTimeout(
          () => {
            console.log('loop device')
            const requestIdleCallback = window.requestIdleCallback || requestAnimationFrame
            requestIdleCallback(() => dispatch('getRunner', { autoTask: true, repeatCount: 0 }))
          },
          30 * 1000,
        )
        // }
        return curDeviceRunner?.status
      } else {
        // 失败流程 - 没有Runner
        console.warn('getRunner - 绑定失败，没有runner')
        commit('update', { checking: false, status: 0, loading: 0 })
        isFirst = false
        if (repeatCount > 0) {
          console.warn('getRunner - 3s后重试(无runner)，剩余次数', repeatCount)
          commit('update', { connecting: true, checking: false, status: 0 })
          setTimeout(() => dispatch('getRunner', {
            repeatCount: repeatCount - 1,
            afterGetRunnerSucceessHideDialog,
          }), 3000)
          // console.log('getRunner 4', err, res)
          // 未绑定设备自动跳转至绑定页
          if (
            router?.currentRoute?.value?.name &&
            ['home', 'heziFiles', 'cinema'].includes(router?.currentRoute?.value?.name as string)
          ) router.push({
            path: '/device-bind',
          })
          return false
        }
        return false
      }
    },
    async config ({ commit }, params = {}) {
      console.log('XL-HEZI')
      const [err, res]: [IErrorRes, ITaskRes] = await request('/drive/v1/task', params, { method: 'PATCH' })
      if (err) return commit('toast', err.error_description)
    },
    setDeviceConfig ({ commit }, params) {
      getInnerApiProxy({
        proxyUrl: 'device/config',
        proxyMethod: 'post',
        proxyData: params,
      })
    },
    async getTasks ({ commit, dispatch, state, getters }, params = {}) {
      const {
        replay = true,
        isReplay = false,
        page_token = '',
        limit = 200,
        refresh = true,
        getAll = false,
      } = params
      const type = state.taskType
      if (type === 'file') return
      if (state.status === 0 || state.loading) return
      commit('update', { loading: isReplay ? 3 : page_token ? 2 : 1 })
      commit('clearTaskTimer')
      // get请求/drive/v1/tasks接口
      // filters中type包含download则响应下载任务，否则获取的是设备列表
      // phase： {in: 'PHASE_TYPE_COMPLETE'}则获取已完成任务
      // phase： {in: "PHASE_TYPE_PENDING,PHASE_TYPE_RUNNING,PHASE_TYPE_ERROR,PHASE_TYPE_PAUSED"}则获取非已完成任务
      const [err, res]: [IErrorRes, ITaskRes] = await request('/drive/v1/tasks', {
        limit,
        page_token,
        space: state.space,
        filters: JSON.stringify({
          phase: getAll ? { in: `${TASK_RUNNING},${TASK_COMPLETE}` } : { in: TASK_TYPE[type] },
          type: { in: `${USER_DOWNLOAD},${USER_DOWNLOAD_URL}` },
          bizGroup: { in: 'EMPTY' },
        }),
      })
      if (err) {
        if (res?.error === 'unauthenticated') {
          commit('update', { status: 0, loading: 0 })
        } else {
          // 修复设备切换无法重载下载任务列表
          commit('update', { loading: 0 })
        }
        return commit('toast', err.error_description)
      }
      // 已完成首次加载,不需要重复闪loading
      const firstLoadTasks = state.firstLoadTasks
      if (firstLoadTasks[type]) {
        firstLoadTasks[type] = false
        commit('update', { firstLoadTasks })
      }
      if (getAll) {
        commit('update', { loading: 0 })
        return res
      }
      if (res.tasks) {
        const inListFiles: string[] = []
        let list = res.tasks.map(v => {
          commit('set', v)
          if (
            v.type === 'user#download' &&
            [
              'PHASE_TYPE_PENDING',
              'PHASE_TYPE_RUNNING',
              'PHASE_TYPE_COMPLETE',
              'PHASE_TYPE_PAUSED',
            ].includes(v.phase)
          ) {
            // 保存取回的原文件列表，以避免重复的取回文件
            inListFiles.push(v.params.file_id)
          }
          return v.id
        })
        if (state.deleteTaskList.length > 0) {
          const tempList = list.filter(v => !state.deleteTaskList.includes(v))
          if (tempList.length === list.length) {
            commit('update', { deleteTaskList: [] })
          } else {
            list = tempList
          }
        }
        if (type === 'ing') {
          const tasks = page_token ? [...state.tasks, ...list] : list
          commit('update', { tasks, inListFiles })
        } else {
          const tasksDone = page_token ? [...state.tasksDone, ...list] : list
          commit('update', { tasksDone, inListFiles })
        }
      }
      commit('update', { loading: 0, nextPageToken: res.next_page_token })
      if (res.expires_in && replay) {
        taskTimmer = setTimeout(
          () => dispatch('getTasks', { isReplay: true }),
          res.expires_in * 1000,
        )
      }
    },
    async getTask ({ commit }, id) {
      const [err, res]: [IErrorRes, ITask | any] = await request(
        `/drive/v1/tasks/${id}`,
        {},
      )
      if (err) {
        commit('toast', err.error_description)
        commit('updateFile', { id, data: { updating: false } })
        return
      }
      if (res.id) {
        commit('set', { ...res, updating: false })
        let spec: any = (res.params.spec || '').match(/(delete|pause|running)/)
        spec = spec ? spec[0] : 'running'
        // commit('toast',`任务${SPEC_TXT[spec]}中`)
      } else {
        commit('toast', res.error_description || '执行失败')
        commit('updateFile', { id, data: { updating: false } })
      }
    },
    async actionPlugin ({ commit, state }, params = {}) {
      const [err, res]: [IErrorRes, ITask | any] = await request(
        '/drive/v1/task',
        params,
        { method: 'POST' },
      )
      if (err) {
        commit('toast', err.error_description)
        return
      }
      return res
    },
    async actionTask ({ commit, state, dispatch }, { id, action, type, willRefresh = true, willCheck = true, willToast = true }) {
      const file = state.all[id] as ITaskFile
      const space = state.space
      const isXlBox = global.isXlBox
      commit('updateFile', {
        id,
        data: { updating: true, update_action: action },
      })
      const statActions = {
        pause: 'downloading_pause_btn_click',
        running: 'downloading_start_btn_click',
      }
      if (action === 'delete') {
        commit('delete', id)
        const delStat =
          state.taskType === 'ing' ? 'downloading_delete_btn_click' : 'complete_delete_btn_click'
        stat({ action: delStat, data: { if_delete_file: 1 } })
      } else {
        stat({ action: statActions[action] })
      }
      const user_id = global.userId
      const params = {
        space,
        type: file.type,
        id: file.id,
        user_id,
        // phase: file.phase,
        // progress: 0,
        // file_id: file.id,
        // file_name: file.name,
        // file_size: file.file_size,
        set_params: {
          spec: JSON.stringify({ phase: action }),
        },
      }
      if (action === 'signout') {
        params.space = ''
      }
      const [err, res]: [IErrorRes, any] = await request(
        '/drive/v1/task',
        params,
        { method: 'PATCH' },
      )
      console.log('PATCH err', err)
      if (err) {
        commit('updateFile', { id, data: { updating: false } })
        commit('toast', err.error_description)
        return false
      }
      if (res.error_description) {
        commit('updateFile', { id, data: { updating: false } })
        commit('toast', res.error_description)
      } else {
        if (action !== 'signout' && willToast) {
          // commit('toast', `任务${SPEC_TXT[action]}中`)
          setTimeout(() => {
            // 盒子不再调用查询任务详情
            // willCheck && dispatch('getTask', isXlBox ? file?.params?.file_id : id)
            !isXlBox && willCheck && dispatch('getTask', id)
            willRefresh && dispatch('getTasks', { refresh: false, isReplay: true })
          }, 2000)
        }
      }
      if (action === 'signout' && type !== 'logout' && willRefresh) {
        // 解绑但不退出登陆时
        setTimeout(() => dispatch('getRunner'), 1000)
      }
      return true
    },
    async delete ({ commit, state }, taskIds) {
      console.log('store delete')
      commit('delete', taskIds)
      // const space = state.space
      // let taskIdQs = ''
      // commit('delete', taskIds)
      // if (Array.isArray(taskIds)) {
      //   taskIdQs = `task_ids=${taskIds.join('&task_ids=')}`
      // } else {
      //   taskIdQs = `task_ids=${taskIds}`
      // }
      // const [err, data]: [IErrorRes, any] = await request(
      //   `/drive/v1/tasks?space=${encodeURIComponent(space)}&${taskIdQs}`,
      //   {},
      //   { method: 'DELETE' },
      // )
      // console.log('delete', err)
      // if (err) {
      //   return commit('toast', err.error_description)
      // }
      // if (data.message) {
      //   commit('toast', data.message)
      // }
    },
    async deleteRunner ({ commit, state }, id) {
      const [err, data]: [IErrorRes, any] = await request(`/drive/v1/tasks?task_ids=${id}`, {}, { method: 'DELETE' })
      if (err) {
        return commit('toast', err.error_description)
      }
      if (data.message) {
        commit('toast', data.message)
      } else {
        const runners = state.runners.filter(v => v !== id)
        commit('update', {
          runners,
        })
      }
    },
    async download ({ commit, state, dispatch }, { file, type = USER_DOWNLOAD, downloadPath = '', willCheck = true, prevParams = {} }) {
      const space = state.space
      const user_id = global.userId
      const envStore = useEnvStore()
      // 创建远程下载和云添加
      const params: any = {
        ...prevParams,
        target: space,
        platform: envStore.platform,
      }
      const statData = {
        task_type: 'getback',
        url: '',
        result: 'fail',
        gcid: file.id,
        file_name: file.name,
        file_suffix: file.file_extension.replace('.', '') || '',
        file_size: file.size,
        cpeerid: '',
      }
      if (type === USER_DOWNLOAD_URL) {
        params.url = file.url
        params.total_file_count = String(file.file_count)
        params.parent_folder_id = state.pathFolderId
        statData.task_type = 'download'
        statData.url = file.url
      } else {
        params.file_id = file.id
        params.parent_folder_id = state.pathFolderId
      }
      if (file.ids && file.ids.length) {
        params.sub_file_index = file.ids.join(',')
      }
      if (file.sub_file_index) {
        // 重试时继承子文件的选择
        params.sub_file_index = file.sub_file_index
      }
      console.log('download-hezi', file)
      const [err, data]: [IErrorRes, any] = await request(
        '/drive/v1/task',
        {
          space,
          type,
          file_name: file.name,
          file_size: file.size,
          params,
          index: file.index,
          user_id,
          indexs: file.indexs,
          downloadPath,
        },
        { method: 'POST' },
      )
      if (err) {
        if (err.error === 'task_daily_create_limit') {
          commit('pop', {
            show: true,
            title: '今日下载额度已用完',
            tip: '非会员每日可免费创建3个下载任务，会员不限任务数（白金/超会均可）',
            okTxt: '开通会员',
            type: 'vip',
          })
        } else {
          commit('toast', err.error_description || '创建任务失败')
        }
        stat({
          action: 'create_download_task',
          data: statData,
        })
        logInfo('create_download_task_Fail', { file_name: file.name, downloadPath, err })
        return
      }
      console.log('play', file, data)
      if (data && data.task) {
        commit('toast', '任务创建成功')
        data.task.file_size = file.indexs?.reduce((totalSize, item) => {
          totalSize += item.size
          return totalSize
        }, 0) || '0'
        data.task.file_name = file.name
        if (data.task?.params) data.task.params.url = file.url
        commit('set', data.task)
        commit('update', {
          tasks: [data.task.id, ...state.tasks],
        })
        statData.result = 'success'
        type === USER_DOWNLOAD_URL && await dispatch('createDownloadPath', { file_id: state.pathFolderId })
        // type === USER_DOWNLOAD_URL && setTimeout(() => dispatch('checkHuddle', data.task), 1500)
      } else {
        commit('toast', data.error_description || '创建任务失败')
        logInfo('create_download_task_Fail', { file_name: file.name, downloadPath, err: data })
      }
      stat({
        action: 'create_download_task',
        data: statData,
      })
    },

    async getResource ({ commit }, { urls, downloadPath = '', onlyVideo = false }) {
      console.log('getResource onlyVideo', onlyVideo)
      const user_id = global.userId
      const [err, data]: [IErrorRes, any] = await request(
        '/drive/v1/resource/list',
        {
          page_size: 100000,
          urls,
          userid: user_id,
          downloadPath,
          onlyVideo,
        },
        { method: 'POST' },
      )
      commit('update', { taskType: 'ing' })
      if (err) return err
      return data
    },
    async getFileIndex ({ }, params) {
      const [err, data]: [IErrorRes, any] = await request(
        `/drive/v1/resource/indices?list_id=${params.listId}${params.include}`,
      )
      if (err) return err
      return data
    },
    async close ({ commit }) {
      const envStore = useEnvStore()
      stat({ action: 'return_shoulei_btn_click' })
      if (runnerTimer) clearTimeout(runnerTimer)
      commit('clearTaskTimer')
      const isMiniProgram = await isMiniProgramPromise()
      if (isAndroid) {
        setTimeout(() => sendMessage({ platform: 'android', name: 'xlFinishSelfActivity' }, { noCallback: true }), 500)
      } else if (isIOS) {
        // 当前线上不支持， xlCloseView 1.3.3起支持
        setTimeout(() => sendMessage({ platform: 'ios', name: 'xlCloseView' }), 500)
      } else if (isNativeApp() || isIframe()) {
        nativeCall('jumpHome')
      } else if (isMiniProgram) {
        const url = MapMiniProgramHome[envStore.platform] || '/pages/index/index'
        window.wx.miniProgram.reLaunch({ url })
      } else {
        window.close()
      }
    },
    async checkHuddle ({ getters, commit, state }, task) {
      console.log('创建任务后 查询抱团, 将自动抱团')
      const peer_id = global.peerid
      const user_id = global.userId
      const isSuperVip = getters.isSuperVip
      const device = global.device
      const url = task.params.url || ''
      const guid = task.params.guid || ''
      const bt_flag = url.includes('magnet') ? 1 : 0
      const ua = navigator.userAgent
      const isSafari = ua.includes('Safari') && ua.indexOf('Chrome') < 0 ? true : false
      console.log('isSafari', isSafari)
      const res: any = await fetch(
        `${TeamSpeedApi}/number?client_name=nas_h5&client_version=${device.versionCode}&release_version=${device.appVersion}&r=${Date.now()}&debug=true`,
        {
          method: 'POST',
          body: JSON.stringify({
            peer_id,
            user_id,
            bt_flag,
            gcid_or_infohash: guid,
            url,
          }),
        },
      ).then(res => res.json())
      console.log('抱团信息', res)
      const teamLeftTimes = res.team_left_times

      if (res.result === 50 || res.result === 51) {
        // teamLeftTimes = 0 // TODO: 仍需要显示次数
        // setTimeout(() => toast(res.message), (teamLeftTimes > 0 ? 2000 : 100))
      }
      const showLastCount = state.teamLeftTimes !== teamLeftTimes
      if (res.team_number) {
        // TODO: 有抱团信息就转移显示
        commit('update', {
          showLastCount,
          teamNumber: res.team_number,
          teamLeftTimes: isSuperVip ? teamLeftTimes : 0,
          // huddleNum,
          teamTaskIds: [task.id, ...state.teamTaskIds],
          teamTaskId: task.id,
        })
      }
    },
    async privilegeNasFull ({ commit }) {
      const [err, data]: [IErrorRes, any] = await request(
        '/drive/v1/privilege/PAN_CLI_FULL',
        {})
      if (err) return commit('toast', err.error_description)
      console.log(data)
      commit('update', {
        panCliFull: data.result === 'ACCEPTED',
      })
    },
    async checkPrivilegeAuth ({ commit, state, getters, dispatch }) {
      const [err, data]: [IErrorRes, any] = await request('/drive/v1/privilege/PAN_CLI_PREVIEW')
      if (err) return commit('toast', err.error_description)
      console.log('PAN_CLI_PREVIEW=========>', data)
      commit('update', {
        privilegeAuth: data.result === 'ACCEPTED',
      })

    },
    async getDeviceInfoWatch ({ commit, dispatch, state, getters }, params = {}) {
      const { repeatCount = 6 } = params
      // console.log('repeatCount233', repeatCount, isFirst)

      const userId = getters.userId
      // const runnerConfig = getters.runnerConfig

      try {
        const proxyRes: any = await getInnerApiProxy({
          proxyUrl: 'device/info/watch',
          proxyMethod: 'post',
          proxyParams: { device_space: state.curDeviceRunner.target, user: state.user },
          proxyData: { user: state.user },
        })

        const expire_time = proxyRes?.data?.runner_status?.expire_config?.expire_time ? dayjs(proxyRes.data.runner_status.expire_config.expire_time).format('YYYY-MM-DD HH:mm:ss') : '' // 过期时间
        const ntp_time = dayjs(proxyRes.data?.ntp_time || new Date()).format('YYYY-MM-DD HH:mm:ss') // 当前时间
        const expiredDay = dayjs(expire_time).diff(ntp_time, 'day') // 距离到期天数
        const regIsExpire = proxyRes?.data?.runner_status?.expire_config?.reg_is_expire === 'true' // 合作协议已经过期
        const userIdMap = getLocalValue('userIdMap') || {}
        let cooperationScene: string | number = ''
        let cooperationExpiresDialogVisiable = false
        if (!proxyRes?.data?.labels?.includes('withPreviewPrivilege')) { // 不带激活码 要填写邀请码弹窗====》 群晖 威联通设备
          if (repeatCount > 0) {
            if (getters.isVip && regIsExpire) {
              console.log('a.会员用户  b.合作协议已经过期 c.当前账号只弹一次')
              cooperationScene = 1
              if (!userIdMap[userId]) {
                cooperationExpiresDialogVisiable = true
              } else {
                if (userIdMap[userId]) {
                  const currentUsrIsShow = userIdMap[userId]['show'] || ''
                  if (currentUsrIsShow) {
                    // 当前账号已经弹窗一次 就不在弹窗了
                    cooperationExpiresDialogVisiable = false
                  } else {
                    // 当前账号没弹窗过 就弹窗一次
                    cooperationExpiresDialogVisiable = true
                  }
                } else {
                  //非同一个用户
                  cooperationExpiresDialogVisiable = true
                }
              }
            } else {
              if (expiredDay >= 0 && EXPIRE_DATE_LIST.includes(expiredDay)) {
                console.log('非会员用户 距离到期时间还有15天、7天、6天、5天、4天、3天、2天、1天、0天时，当天弹出一次 到期时间为 T 距离到期时间还有15天，即T-15 距离到期时间还有1天，即T-1 每个账号弹一次')
                cooperationScene = 2
                if (!userIdMap[userId]) {
                  cooperationExpiresDialogVisiable = true
                } else {
                  if (userIdMap[userId]) {
                    const currentUsrMapExpiredDayList = userIdMap[userId]['expiredDay'] || []
                    if (currentUsrMapExpiredDayList.includes(expiredDay)) {
                      cooperationExpiresDialogVisiable = false
                    } else {
                      cooperationExpiresDialogVisiable = true
                    }
                  }
                }
              }
            }
          } else { // 登录后 status 状态 由 “” 变为 need_preview_privilege 状态 需要时间 所以先延迟一分钟
            if (proxyRes.data.runner_status.status === 'need_preview_privilege') { // 判断方式不一样 似乎是因为取不到runner_status.expire_config
              if (!getters.isVip) {
                // 关联激活码/nasid已经过期/失效 → 非会员到期 无法使用需要开会员弹窗
                cooperationScene = 0
                // 非会员合作弹窗到期
                cooperationExpiresDialogVisiable = true
              }
            }
          }
        }
        commit('update', {
          deviceInfoWatchData: proxyRes.data,
          cooperationExpiresDialogVisiable,
          cooperationScene,
          expiredDay,
        })
      } catch (error) {
        console.log(error)
      }
      // if (repeatCount >= 0) {
      //   setTimeout(() => dispatch('getDeviceInfoWatch', {
      //     repeatCount: repeatCount - 1,
      //   }), 10000)
      // }
    },
    async createDownloadPath ({ commit }, params) {
      getInnerApiProxy({
        proxyUrl: 'device/download_path',
        proxyMethod: 'post',
        proxyData: params,
      })
    },
    async getDeviceDownloadPath ({ commit, dispatch }) {
      const proxyRes = await getInnerApiProxy({
        proxyUrl: 'device/download_paths',
        proxyMethod: 'get',
      })
      let list = []
      if (proxyRes && proxyRes.data) {
        list = proxyRes.data.map((v) => {
          return {
            id: v.Id,
            title: v.RealPath,
            icon: Icons.dir,
            isWrite: true,
            isLoaded: false,
            children: [],
            showClose: true,
            ...v,
          }
        })
      }
      commit('update', {
        historyDownloadPath: list,
      })
    },
    async deleteDeviceDownloadPath ({ commit }, fileId) {
      const proxyRes = await getInnerApiProxy({
        proxyUrl: `device/download_path/${fileId}`,
        proxyMethod: 'delete',
        proxyData: {},
      })
      return proxyRes.data
    },
    async getFiles ({ commit, state, dispatch }, params) {
      const {
        isReplay = false,
        page_token = '',
        limit = 200,
        parent_id = '',
        name = '',
        isBack = false,
        isBackHome = false,
      } = params
      commit('update', { loading: isReplay ? 3 : page_token ? 2 : 1 })
      const [err, data]: [IErrorRes, any] = await request('/drive/v1/files', {
        limit,
        space: state.space,
        page_token,
        parent_id,
      })
      if (err) {
        // 进入BT文件夹失败时，重新拉回已完成的任务
        dispatch('getTasks', { type: 'done' })
        commit('update', { taskType: 'done' })
        return commit('toast', err.error_description || err.msg)
      }
      console.log('进入BT文件夹', data)

      if (data && data.files) {
        const ids = data.files.map(f => {
          commit('set', f)
          return f.id
        })
        const files = page_token && !isBack ? [...state.files, ...ids] : ids
        let parentIds = [...state.parentIds]
        if (parent_id) {
          if (isBack) {
            parentIds.pop()
          } else if (isBackHome) {
            parentIds = parentIds.splice(0, 1)

          } else {
            parentIds.push({ id: parent_id, name })
          }
        } else {
          parentIds = []
        }
        commit('update', {
          files,
          parentIds,
        })
      } else {
        return commit('toast', (ERROR_DESC_MAP[data.error] || data.error_description) + ` [${data.error_code}]`)
      }
      commit('update', { loading: 0 })
    },
    /**
     * 虽然叫getFileInfo但是实际是播放
     */
    async getFileInfo (
      { commit, state, getters },
      params: {
        id: string
        type?: string
        usage?: number
        theme?: 'light' | 'dark'
        scene?: API_DEVICE.PlayScene
        apiUrl?: string
        size?: number
        path?: string
        name?: string
      }) {

      const {
        id, type = '', usage = 0, theme = 'light', scene = 'nfo', apiUrl = '', name, path = '',
        ...args
      } = params

      const space = state.space
      const isVip = getters.isVip
      const isSuperVip = getters.isSuperVip
      const [err, data]: [IErrorRes, API_FILE.DriveFile] = await request(`${apiUrl || '/drive/v1/files'}/${id}`, {
        space,
        // with 可选值： 'playscene=nfo' | 'playscene=download_list'
        usage,
        with: `playscene=${scene}`,
        path: path,
        ...args,
      })
      console.log('请求播放文件', err, data, usage, name)
      reportPlayVideo(id, 0, { path: data.file_path })
      if (err) {
        console.log('err', err)
        stat({
          action: 'completed_player_launch',
          data: { result: 'fail' },
        })
        // 超出播放次数限制
        if (err.error_description === 'PLAY_COUNT_LIMIT') return openVipPlayTimes({ theme })
        // 这个丢回上层处理的原因是，不同场景可能有不同的处理方式
        // 如果是个人影院中播放，报文件找不到时，需要删除刮削结果
        if (err.error_description === 'file_not_found') throw err
        return commit('toast', '文件不存在，请核对后重试')
      }
      // 如果ID为空
      if (!data.id) return toast('文件内容加载失败，请稍后重试')
      if (isHeziPCClient() && name && !isImageFile(name)) {
        let playUrl = data.web_content_link as string
        const ip = state.curDeviceRunner.params?.lan_ip
        console.log('ip', ip)
        let nearPlayUrl
        if (ip && data?.links) {
          console.log('link:', data?.links[`application/local-${ip}`])
          nearPlayUrl = data?.links[`application/local-${ip}`]?.url
          console.log('nearPlayUrl:', nearPlayUrl)
          if (nearPlayUrl) {
            const isNear = await isNearBy(ip)
            if (isNear) playUrl = nearPlayUrl
          }
        }
        return playVideo(playUrl, JSON.stringify({...data, name}))
      }
      // usage === 2 - 播放
      // 不在厂商App，不在小程序，不在iOS迅雷，不在安卓迅雷，弹窗引导到手雷播放
      if (
        !isNativeApp() && !isIOS && !isAndroid && 
        !state.isMiniProgram && 
        [2, 'PLAY'].includes(usage) && 
        !window.navigator.userAgent.includes('TBC') ||
        name && isImageFile(name)
      ) {
        console.log('web端播放')
        return openFileProcessor({
          isShowPreviewFileComp: true,
          previewFileInfo: { ...data, name: name },
        })
      }

      // TODO: 无法判断是bt，需要从icon判断
      const isVideo = (data?.mime_type?.includes('video') || data?.mime_type?.includes('application/octet-stream') || isTruePlayFormat(data?.name ?? '')) && type !== 'bt'
      // TODO: 需要匹配下文件类型
      console.log('links', data, isVideo, window.navigator.userAgent.includes('TBC'))

      if (isVideo && window.navigator.userAgent.includes('TBC')) {
        console.log('走迅雷客户端播放')
        playInThunder({ space: `hezi_device_id#${space}`, fileID: id, userID: state.user?.userID ?? '' })
      }

      if (data && data.links) {
        const realPath = data?.params?.RealPath
        const links = Object.keys(data.links).map(v => data?.links?.[v]?.url)
        let link = links.filter(v => new URL(v ?? '').pathname.indexOf('/download') === 0)[0] || data.web_content_link
        // TODO: 校验选择内网链接
        if (isNativeApp() || state.isMiniProgram) {
          // TODO: 需要匹配厂商来使用消费链接还是真实路径
          const extData = JSON.stringify({ created_time: data.created_time, modified_time: data.modified_time })
          link = isNativeApp() ? realPath : link
          if (isVideo) {
            const envStore = useEnvStore()
            console.log('isVideo 1', isVideo, OldJumpPlayVideoPlatforms.includes(envStore.pathEnv))
            OldJumpPlayVideoPlatforms.includes(envStore.pathEnv) ?
              nativeCall('jumpPlayVideo', realPath) :
              nativeCall('jumpPlayVideo', realPath, data.name, data.size, extData)
          } else {
            nativeCall('openFile', realPath, data.name, data.size, data.mime_type, extData)
          }
        } else {
          const isAudio = data?.mime_type?.includes('audio') || /\.(mp3|wav)$/i.test(data?.name ?? '')
          if (!isAudio && !isVideo) return
          if (isIOS || isAndroid) {
            // 版本低于3.1.0才应用这个规则。当3.1.0以下版本覆盖率低于1%时，可以删除这个判断
            if (parseVersionToNumber(state?.deviceInfoWatchData?.client_version ?? '0.0.0') < parseVersionToNumber('3.1.0')) {
              if (state?.deviceInfoWatchData) {
                if (!isVip && USER_PLAY_FILE_SIZE < Number(data.size) && VIP_USER_PLAY_FILE_SIZE > Number(data.size)) { // 非会员 大于1
                  return commit('update', { playPrivilege: { show: true, type: 0 } })
                } else if (!isVip && VIP_USER_PLAY_FILE_SIZE < Number(data.size)) { // 非会员 大于4g
                  return commit('update', { playPrivilege: { show: true, type: 1 } })
                } else if (isVip && !isSuperVip && VIP_USER_PLAY_FILE_SIZE < Number(data.size)) { // 白金 大于4g
                  return commit('update', { playPrivilege: { show: true, type: 2 } })
                }
              }
            }
            if (isIOS) {
              link = data.web_content_link
              const isSupportPlay = await checkMessage({ platform: 'ios', name: 'xlOpenPlayer' })
              if (isSupportPlay && isSupportPlay.exist) {
                const params = {
                  playlist: [{
                    fileKind: 0,
                    url: link,
                    name: data.name,
                    fileId: id,
                    from: 'nas',
                    space: state.deviceInfoWatchData.target,
                    mime_type: data.mime_type ?? '',
                    folder_id: data.parent_id ?? '',
                  }],
                }
                sendMessage({ platform: 'ios', name: 'xlOpenPlayer', params })
              } else {
                commit('toast', '请安装最新版手雷来体验云播功能')
              }
              return
            }
            if (isAndroid) {
              link = data.web_content_link
              const device = global.device
              console.log('device', device.versionCode >= SUPPORT_VIDEO_PLAY_CODE)
              if (device.versionCode >= SUPPORT_VIDEO_PLAY_CODE) {
                const urlQuery = new URLSearchParams({ // 这里的query是附加在播放链接后的
                  space: state.deviceInfoWatchData.target ?? '',
                  mime_type: data.mime_type ?? '',
                  folder_id: data.parent_id ?? '',
                  file_id: id,
                }).toString()
                const url = data.web_content_link
                // || (data.web_content_link ?? '').indexOf('?') >= 0 ? `${data.web_content_link}&${urlQuery}` : `${data.web_content_link}?${urlQuery}`
                const queryParams = { // 这里的query是附加在调用接口的scheme链接后的
                  title: data.name ?? '',
                  url,
                  downPlay: 'false',
                }
                // @ts-ignore
                const query = new URLSearchParams(queryParams).toString()
                sendMessage({
                  name: 'xlOpenUrl',
                  platform: 'android',
                  params: {
                    url: `xunleiapp://xunlei.com/videoPlay?${query}`,
                    from: 'nas',
                    openType: 1,
                  },
                })
              } else {
                const queryParams = {
                  file_id: id,
                  space: state.deviceInfoWatchData.target ?? '',
                  mime_type: data.mime_type ?? '',
                  folder_id: data.parent_id ?? '',
                }
                const query = new URLSearchParams(queryParams).toString()
                sendMessage({
                  name: 'xlVideoPlay',
                  platform: 'android',
                  params: {
                    title: data.name ?? '',
                    url: link + `&${query}`,
                    fileSize: Number(data.size),
                    playFrom: 'nas',
                    downPlay: false,
                  },
                }, { noCallback: true })
              }

            }
          }
        }

        stat({
          action: 'completed_player_launch',
          data: { result: 'success' },
        })
      }
    },
    async getPaths ({ commit, state }, id) {
      const space = state.space
      const [err, data]: [IErrorRes, any] = await request('/drive/v1/files', {
        space,
        parent_id: id === '0' ? '' : id,
        filters: JSON.stringify({ kind: { eq: 'drive#folder' } }),
      })
      if (err) return commit('toast', err.error_description)
      if (data.files) {
        return data.files
      }
      return []
    },
    /**
     * type: 要定位到的面板，3 - 开通白金会员; 5 - 开通超级会员
     */
    goVip ({ commit, state, getters }, { type = 0, position = '0' }) {
      const envStore = useEnvStore()
      const client = judgeClient() // 客户端 ios an pc mac
      const plm = envStore.plm || global.plm || 'unknown' // 厂商缩写
      const visit = getReferVisit()
      const isVip = getters.isVip
      const textType = type === 5 ? 'supervip' : isVip ? 'supervip' : 'baijin' // 优先使用传入的type，其次根据是否是会员判断

      // important
      const aidfrom = `${plm}_${visit}_pg_${position}`
      const referfrom = `v_${client}_nas_ggong`
      console.log('打开支付页：aidfrom:', aidfrom, 'referfrom', referfrom)

      if (state.platform === 'hiksemi' || state.isMiniProgram) {
        return commit('toast', '请到迅雷会员官网开通')
      }
      if (isAndroid) {
        sendMessage({
          platform: 'android',
          name: 'xlPay',
          params: {
            payType: 7,
            aidfrom,
            reportRefer: referfrom,
            vasType: type ? type : isVip ? 5 : 3, // 传入5是定位到超级会员
          },
        }, { noCallback: true })
        return
      }
      if (isIOS) {
        /**
         * 这里要区分的原因是，手雷内部浏览器不支持打开 universalLink，所以只能走JSBridge
         */
        sendMessage({
          platform: 'ios',
          name: 'xunleiQuery',
          params: {
            target: 'user',
            action: 'vipPurchase',
            params: {
              payconfid: '7',
              referfrom,
              aidfrom,
              selectedTab: textType,
            },
          },
        }, { noCallback: true })
        return
      }
      if (isIOSUA) {
        // http://wiki.xunlei.cn/pages/viewpage.action?pageId=41619640
        callIOS({
          target: 'user',
          action: 'vipPurchase',
          params: {
            referfrom,
            aidfrom,
            payconfid: '7',
            selectedTab: textType,
          },
          from: 'nas',
        })
      } else {
        // 只显示微信或支付宝配置 payways=wechat或zfb
        const payLink = `https://pay.xunlei.com/pages/2020/common-pay/?referfrom=${referfrom}&aidfrom=${aidfrom}&default_tab=${textType}`
        // 这里通过 i.xunlei.com 跳转的原因是 支付页没有接入session sdk
        // 如果session过期，自己无法刷新，而session有效期只有一天。会导致进入支付页后没有登录态的问题
        // 通过账号页面跳转，会在账号页使用token换session的方式刷新session，从而规避问题
        // 后续还是需要支付页接入session sdk，否则返回页面的体验比较差
        window.open(`https://i.xunlei.com/center/account/personal/auth/?redirect_uri=${encodeURIComponent(payLink)}&ui_client_id=${packageJSON.xbase[APP_NAME].clientId}&ui_client_key=c`, '_blank')
      }
    },
    goXunlei ({ commit, state }) {
      if (isIOS) {
        location.assign('https://oia-pan-ssl.xunlei.com/')
      } else if (isAndroid) {
        callAndroid({
          target: '',
          action: '',
          params: {},
          timeout: 3000,
          // 盒子跳转至官网下载
          callback: () => { location.assign(APP_NAME === 'hezi' ? 'http://hezi.xunlei.com/dl' : 'http://mobile.xunlei.com/') },
        })
      } else {
        // 盒子跳转至官网下载
        location.assign(APP_NAME === 'hezi' ? 'http://hezi.xunlei.com/dl' : 'http://mobile.xunlei.com/')
      }
    },
    async getConfig ({ commit, dispatch }) {
      const result = await getConfig({
        'peer-id': global.userId,
      })
      console.log('getConfig=====>', result)
      if (result && result.values && result.values.h5) {
        const { devices_control = false, download_path = false, download_speed = false, show_quit = false } = result.values.h5
        commit('update', {
          // showDevicesControl: devices_control,
          showDevicesControl: true,
          showPathControl: download_path,
          // showSpeedControl: download_speed,
          showQuitControl: show_quit,
        })
      }
      // dispatch('privilegeNasFull')
    },
    async deviceAuthorize ({ commit, dispatch }, params) {
      const [err, data]: [IErrorRes, any] = await request(apiOrigin + '/v1/user/device/authorize', params, { method: 'POST' })
      if (err) {
        commit('update', { status: 0, loading: 0, checking: false, connecting: false })
        commit('toast', err.error_description)
        return
      }
      return data
    },
    async authSignOut () {
      const proxyRes = await getInnerApiProxy({
        proxyUrl: 'auth/signout',
        proxyMethod: 'post',
        proxyData: {},
      })
      return proxyRes
    },
    async logout ({ commit, state, dispatch, getters }, isLogout) {
      const id = state.curDeviceRunner?.id ?? ''
      const envStore = useEnvStore()
      const device = state.all[id]
      if (isLogout) {
        if (state.deleteCompletely && state.curDeviceRunner?.id && state.status) {
          await dispatch('authSignOut')
        }

        removeLocalValue('curDeviceRunner')
        console.log('isSeparateAccount', isSeparateAccount)
        if (isSeparateAccount) {
          await auth.signOut()
          router.replace('/login')
          commit('dialog', { show: false })
        } else {
          // 消除多余参数，防止切换帐号时提示帐号不匹配,不过要加上plm,方便在close页面里跳转会对应的小程序路径
          const platform = FactoryPathMap[envStore.platform] ? `${FactoryPathMap[envStore.platform]}/` : ''
          const redirectUrl = location.origin + `/yc/${platform}close/?plm=${envStore.plm ?? ''}&platform=${envStore.platform ?? ''}`
          sso.signOut(redirectUrl)
        }
      } else {
        stat({
          action: 'mytab_page_click',
          data: { click_id: 'exit_login' },
        })
        const canUnbind = Boolean(device && device.name && device.space !== 'platform#pcxllite')
        commit('update', { deleteCompletely: canUnbind })
        commit('dialog', {
          show: true, title: '退出登录',
          // tip: canUnbind ? `同时解绑【${device.name}】` : '',
          // more: canUnbind ? '*解绑后可以重新绑定其他迅雷帐号' : '',
          okTxt: '确定',
          cancelTxt: '取消',
          action: 'logout',
          hasCheck: canUnbind ? true : false,
          showLoader: true,
          loadingTxt: '账号退出中...',
        })
      }
    },
  },
})

export function useStore () {
  return baseUseStore(key)
}
